import React from 'react';
import {
  Action,
  ActionId,
  ActionImpl,
  KBarAnimator,
  KBarPortal,
  KBarPositioner,
  KBarProvider,
  KBarResults,
  KBarSearch,
  useMatches,
} from 'kbar';
import history from 'src/libs/history';
import { Icon } from 'semantic-ui-react';
import { isAdminSelector } from 'src/utils/helper';
import { useAppSelector } from 'src/store';
import { PATH } from 'src/constants/app';
// import { DirectoryFilter } from 'src/store/model/role';

const CommandPalette: React.FC = ({ children }) => {
  const isAdmin = useAppSelector(isAdminSelector);

  const initialActions: Action[] = [
    {
      id: 'homeAction',
      name: 'Home',
      keywords: 'home',
      section: 'Navigation',
      perform: () => history.push(PATH.HOME),
      icon: <Icon name="home" />,
      subtitle: 'View all available courses',
    },
    {
      id: 'myCourseActions',
      name: 'My courses',
      keywords: 'my course',
      section: 'Home',
      perform: () => history.push(`${PATH.HOME}?tab=my_courses`),
      parent: 'homeAction',
    },
    {
      id: 'allCourseActions',
      name: 'All courses',
      keywords: 'all course',
      section: 'Home',
      perform: () => history.push(`${PATH.HOME}?tab=all_courses`),
      parent: 'homeAction',
    },
    // {
    //   id: 'directoriesActions',
    //   name: 'Directory',
    //   keywords: 'directory',
    //   section: 'Navigation',
    //   icon: <Icon name="users" />,
    //   subtitle: 'Connect with members of the Insignia community.',
    //   perform: () => history.push(`/${PATH.DIRECTORY}`),
    // },
    // {
    //   id: 'mentorActions',
    //   name: 'Mentors',
    //   keywords: 'mentors',
    //   section: 'Directory',
    //   perform: () =>
    //     history.push(`/${PATH.DIRECTORY}/${DirectoryFilter.Mentors}`),
    //   parent: 'directoriesActions',
    // },
    // {
    //   id: 'expertsActions',
    //   name: 'Experts',
    //   keywords: 'mentors',
    //   section: 'Directory',
    //   perform: () =>
    //     history.push(`/${PATH.DIRECTORY}/${DirectoryFilter.Experts}`),
    //   parent: 'directoriesActions',
    // },
    // {
    //   id: 'alumniActions',
    //   name: 'Alumni',
    //   keywords: 'alumni',
    //   section: 'Directory',
    //   perform: () =>
    //     history.push(`/${PATH.DIRECTORY}/${DirectoryFilter.Alumni}`),
    //   parent: 'directoriesActions',
    // },
    {
      id: 'faqActions',
      name: 'FAQs',
      keywords: 'faqs',
      section: 'Navigation',
      subtitle: 'Have questions on our courses? Get answers here.',
      icon: <Icon name="info circle" />,
      perform: () => history.push(`/${PATH.FAQS}`),
    },
    {
      id: 'settingActions',
      name: 'Setting',
      keywords: 'setting',
      section: 'Navigation',
      subtitle: 'Edit your bio, subscriptions, notifications and more.',
      icon: <Icon name="setting" />,
      perform: () => history.push(`/${PATH.SETTING}`),
    },
    {
      id: 'profileAction',
      name: 'Profile',
      keywords: 'profile',
      section: 'Setting',
      perform: () => history.push(`/${PATH.SETTING}/profile`),
      parent: 'settingActions',
    },
    {
      id: 'accountAction',
      name: 'Account',
      keywords: 'account',
      section: 'Setting',
      perform: () => history.push(`/${PATH.SETTING}/account`),
      parent: 'settingActions',
    },
    {
      id: 'passwordAction',
      name: 'Password',
      keywords: 'password',
      section: 'Setting',
      perform: () => history.push(`/${PATH.SETTING}/password`),
      parent: 'settingActions',
    },
    {
      id: 'billingAction',
      name: 'Billing',
      keywords: 'billing',
      section: 'Setting',
      perform: () => history.push(`/${PATH.SETTING}/billing`),
      parent: 'settingActions',
    },
    {
      id: 'subscriptionsActions',
      name: 'Subscriptions',
      keywords: 'subscriptions',
      section: 'Setting',
      perform: () => history.push(`/${PATH.SETTING}/subscriptions`),
      parent: 'settingActions',
    },
    {
      id: 'notificationActions',
      name: 'Notifications',
      keywords: 'notifications',
      section: 'Setting',
      perform: () => history.push(`/${PATH.SETTING}/notifications`),
      parent: 'settingActions',
    },
  ];

  if (isAdmin) {
    initialActions.push({
      id: 'archivedCourseActions',
      name: 'Archived courses',
      keywords: 'archived course',
      section: 'Home',
      perform: () => history.push(`${PATH.HOME}?tab=archived_courses`),
      parent: 'homeAction',
    });
  }

  return (
    <KBarProvider
      actions={initialActions}
      options={{ animations: { enterMs: 300, exitMs: 200 } }}
    >
      <KBarPortal>
        <div className="cmd-k">
          <KBarPositioner className="positioner">
            <KBarAnimator className="animator">
              <KBarSearch
                defaultPlaceholder="What are you looking for?"
                className="search"
              />
              <RenderResults />
            </KBarAnimator>
          </KBarPositioner>
        </div>
      </KBarPortal>
      {children}
    </KBarProvider>
  );
};

function RenderResults() {
  const { results, rootActionId } = useMatches();

  return results.length ? (
    <KBarResults
      items={results}
      onRender={({ item, active }) =>
        typeof item === 'string' ? (
          <div className="group">{item}</div>
        ) : (
          <ResultItem
            action={item}
            active={active}
            currentRootActionId={rootActionId!}
          />
        )
      }
    />
  ) : (
    <React.Fragment>
      <div className="d-flex flex-col justify-center align-center p-24">
        <Icon color="purple" name="search" />
        <span className="fw-bold text-primary mt-6">{`No items have been found.`}</span>
      </div>
    </React.Fragment>
  );
}

const ResultItem = React.forwardRef(
  (
    {
      action,
      active,
      currentRootActionId,
    }: {
      action: ActionImpl;
      active: boolean;
      currentRootActionId: ActionId;
    },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const ancestors = React.useMemo(() => {
      if (!currentRootActionId) return action.ancestors;
      const index = action.ancestors.findIndex(
        (ancestor) => ancestor.id === currentRootActionId,
      );
      // +1 removes the currentRootAction; e.g.
      // if we are on the "Set theme" parent action,
      // the UI should not display "Set theme… > Dark"
      // but rather just "Dark"
      return action.ancestors.slice(index + 1);
    }, [action.ancestors, currentRootActionId]);

    return (
      <div ref={ref} className={`item ${active ? 'active' : ''}`}>
        <div className="item-wrapper">
          {action.icon && action.icon}
          <div className="item-content">
            <div>
              {ancestors.length > 0 &&
                ancestors.map((ancestor) => (
                  <React.Fragment key={ancestor.id}>
                    <span
                      style={{
                        opacity: 0.5,
                        marginRight: 8,
                      }}
                    >
                      {ancestor.name}
                    </span>
                    <span
                      style={{
                        marginRight: 8,
                      }}
                    >
                      &rsaquo;
                    </span>
                  </React.Fragment>
                ))}
              <span>{action.name}</span>
            </div>
            {action.subtitle && (
              <span className="item-subtitle">{action.subtitle}</span>
            )}
          </div>
        </div>
        {/*{action.shortcut?.length ? (*/}
        {/*  <div*/}
        {/*    aria-hidden*/}
        {/*    style={{ display: 'grid', gridAutoFlow: 'column', gap: '4px' }}*/}
        {/*  >*/}
        {/*    {action.shortcut.map((sc) => (*/}
        {/*      <kbd*/}
        {/*        key={sc}*/}
        {/*        style={{*/}
        {/*          padding: '4px 6px',*/}
        {/*          background: 'rgba(0 0 0 / .1)',*/}
        {/*          borderRadius: '4px',*/}
        {/*          fontSize: 14,*/}
        {/*        }}*/}
        {/*      >*/}
        {/*        {sc}*/}
        {/*      </kbd>*/}
        {/*    ))}*/}
        {/*  </div>*/}
        {/*) : null}*/}
      </div>
    );
  },
);

ResultItem.displayName = 'resultItem';

export default CommandPalette;
